import 'twin.macro';
import React, { useEffect, RefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAIGenerationContext } from 'components/Editor/AIAssist/contexts/AIGenerationContext';
import { useConversationHistory } from 'components/Editor/AIAssist/hooks/useConversationHistory';
import {
  AIGeneratedDisplay,
  AIGeneratedDisplayProps,
} from 'components/AI/AIGeneratedDisplay';
import { AIPanelType } from 'components/Editor/EditorAIWrapper';
import { FroalaEditorInstance } from 'components/Editor/froalaTypes';
import { useHandlePdfAddAttachment } from 'components/Editor/AIAssist/hooks/useHandlePdfAddAttachment';
import { lineBreak, placeholderLine } from 'i18n/components';

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  froalaInstanceRef: RefObject<FroalaEditorInstance | null>;
  onApply: (data: string) => void;
  onClose: () => void;
  visible?: boolean;
  onPdfAddAttachment?: (file: File) => Promise<void>;
  promptPlaceholder?: AIGeneratedDisplayProps['placeholder'];
  promptDescription?: AIGeneratedDisplayProps['promptDescription'];
  panelState: AIPanelType;
  setPanelState: (state: AIPanelType) => void;
}

export function PromptPanel({
  containerRef,
  froalaInstanceRef,
  onApply,
  onClose,
  onPdfAddAttachment,
  visible = true,
  promptDescription,
  promptPlaceholder,
  panelState,
  setPanelState,
}: Props) {
  const { t } = useTranslation(['common']);
  const { generate, data, isLoading, reset, hasData } =
    useAIGenerationContext();
  const { history, addMessage, clear: clearHistory } = useConversationHistory();
  const { handlePdfAddAttachment, isLoading: isPdfLoading } =
    useHandlePdfAddAttachment(onPdfAddAttachment);

  const isOpen = panelState === 'customPrompt';

  // Add AI responses to conversation history
  useEffect(() => {
    if (hasData && !isLoading) {
      addMessage({ role: 'assistant', content: data });
    }
  }, [hasData, isLoading, data, addMessage]);

  if (!visible || !isOpen) {
    return null;
  }

  function handleSubmitPrompt(prompt: string) {
    addMessage({ role: 'user', content: prompt });
    generate({
      text: '',
      type: 'custom',
      userPrompt: prompt,
      history,
    });
  }

  function handleClose() {
    clearHistory();
    reset();
    setPanelState(null);
    onClose();
  }

  function handleApply() {
    if (!hasData) return;

    const editor = froalaInstanceRef.current;
    if (editor && editor.html && editor.undo) {
      editor.html.insert(data);
      editor.undo.saveStep();
      const editorElement = containerRef.current?.querySelector('.fr-element');
      if (editorElement) {
        onApply(editorElement.innerHTML);
      }
    }

    handleClose();
  }

  return (
    <AIGeneratedDisplay
      promptMode
      onSubmitPrompt={handleSubmitPrompt}
      onIgnore={handleClose}
      onApply={handleApply}
      onApplyAsPdf={
        onPdfAddAttachment
          ? async () => {
              await handlePdfAddAttachment(data);
              handleClose();
            }
          : undefined
      }
      isLoading={isLoading}
      isPdfLoading={isPdfLoading}
      data={data}
      showTypingEffect
      showResourcesButton
      title={
        <>
          {t('ai.customPromptAI')}
          <span tw="text-neutral-moderate font-normal">
            &nbsp; (
            {t('ai.xCredits', {
              count: 3,
            })}
            )
          </span>
        </>
      }
      placeholder={promptPlaceholder}
      promptDescription={
        promptDescription ?? (
          <Trans
            i18nKey="common:ai.promptDescriptions.default"
            components={{
              p: placeholderLine,
              lineBreak,
            }}
          />
        )
      }
    />
  );
}
